import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography, Avatar, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ButtonBasic } from '../../components/button/ButtonBasic.jsx';
import MultiCarousel from '../../components/carousel/MultiCarousel.jsx';
import { IconThreads } from '../../assets/icon/metric/IconThreads.js';
import { dbService } from '../../firebaseInstance';
import { getFirstSentence as getFirstSentenceUtil } from '../../utils/textUtils';
import StaticMapBox from '../../components/map/StaticMapBox.js';

/**
 * CafeDetailModal
 *
 * Props:
 * @param {boolean} opened
 * @param {function} onClose
 * @param {object|null} cafe
 */
export default function CafeDetailModal({ opened, onClose, cafe }) {
  const theme = useTheme();
  const {
    name,
    description,
    threadsUrl,
    cafeUrl,
    magazineParticipant,
    userInfo,
    collectionId,
    registrationType,
    registeredBy,
    cafeIntroduction,
    approvalStatus,
    lat,
    lng,
    address
  } = cafe || {};

  // For magazine participants
  const displayName = userInfo?.profile?.name || '';
  const username = userInfo?.profile?.username || '';
  const avatarUrl = userInfo?.profile?.threads_profile_picture_url || userInfo?.profileImage || '';

  // For regular users
  const registeredByName = registeredBy?.displayName || registeredBy?.username || '';
  const registeredByUsername = registeredBy?.username || '';
  const registeredByAvatar = registeredBy?.profileImage || '';

  const [threads, setThreads] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!opened || !collectionId) { setThreads([]); return; }
        const snap = await dbService.collection('collection').doc(String(collectionId)).get();
        if (!active) return;
        if (snap.exists) {
          const data = snap.data() || {};
          const list = Array.isArray(data.threadList) ? data.threadList : [];
          setThreads(list.slice(0, Math.max(1, list.length)));
        } else {
          setThreads([]);
        }
      } catch (_) {
        if (!active) return;
        setThreads([]);
      }
    };
    load();
    return () => { active = false; };
  }, [opened, collectionId]);


  const threadItems = useMemo(() => {
    return (threads || []).map((t) => ({
      id: t?.id || String(Math.random()),
      text: t?.text || '',
      createdAt: t?.createdAt || null,
    }));
  }, [threads]);

  const getFirstSentence = (text) => getFirstSentenceUtil(text || '');
  const getBodyText = (text) => {
    const first = getFirstSentence(text || '');
    let rest = (text || '').replace(first, '').trim();
    if (rest.startsWith('.') || rest.startsWith('\n')) {
      rest = rest.substring(1).trim();
    }
    return rest;
  };

  return (
    <Dialog open={!!opened && !!cafe} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ typography: 'h5', fontWeight: 800 }}>
        {name || '카페 상세'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* 상단 섹션: 타이틀 정보(설명) + 유저 정보 */}
          <Stack spacing={1}>
            {/* Show approval status badge if pending */}
            {approvalStatus === 'pending' && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.5,
                    bgcolor: 'warning.light',
                    color: 'warning.dark',
                    borderRadius: 1,
                    display: 'inline-block'
                  }}
                >
                  승인 대기 중
                </Typography>
              </Box>
            )}

            {/* Show description for all cafe types (data consistency) */}
            <Box sx={{ display: 'inline-block' }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.7,
                  position: 'relative',
                  display: 'inline-block',
                  '&::before': {
                    content: '"❝"',
                    fontSize: '1.2em',
                    color: 'primary.main',
                    marginRight: '4px'
                  },
                  '&::after': {
                    content: '"❞"',
                    fontSize: '1.2em',
                    color: 'primary.main',
                    marginLeft: '4px'
                  }
                }}
                title={description || ''}
              >
                {description || '설명이 없습니다.'}
              </Typography>
            </Box>

            {/* Show user info - unified approach */}
            {userInfo && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar src={avatarUrl || undefined} sx={{ width: 32, height: 32 }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{displayName}</Typography>
                {username ? <Typography variant="caption" color="text.secondary">@{username}</Typography> : null}
              </Stack>
            )}
          </Stack>

          {/* Static Map */}
          {typeof lat === 'number' && typeof lng === 'number' && (
            <Box>
              <StaticMapBox
                lat={lat}
                lng={lng}
                zoom={14}
                height={200}
                showMarker={true}
                markerColor={theme.palette.primary.main}
              />
              {address && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {address}
                </Typography>
              )}
            </Box>
          )}

          {/* Content Preview: Collection for magazine participants, Threads post for user registrations */}
          {magazineParticipant && collectionId ? (
            // Magazine participant: show collection preview
            threadItems.length > 0 ? (
              <Box>
                <MultiCarousel
                items={threadItems}
                visible={{ xs: 1, sm: 1, md: 1, lg: 1 }}
                gap={12}
                step={1}
                renderItem={(item) => (
                  <Box sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: 'background.paper',
                    height: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                  }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      title={getFirstSentence(item.text)}
                    >
                      {getFirstSentence(item.text) || '제목 없음'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      title={getBodyText(item.text)}
                    >
                      {getBodyText(item.text) || '본문 미리보기가 없습니다.'}
                    </Typography>
                  </Box>
                )}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">컬렉션 프리뷰가 없습니다.</Typography>
            )
          ) : registrationType === 'user' && cafe?.cafeIntroduction ? (
            // User registration: show Threads post
            <Box sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              backgroundColor: 'background.paper',
              minHeight: 120,
              position: 'relative'
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <IconThreads size={14} />
                Threads 포스트
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  color: 'text.primary'
                }}
              >
                {cafe.cafeIntroduction}
              </Typography>
              {cafe.postedAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {new Date(cafe.postedAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} 포스팅
                </Typography>
              )}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'space-between' }}>
          <ButtonBasic size="md" variant="text" onClick={onClose}>닫기</ButtonBasic>
          <Stack direction="row" spacing={1.5}>
            {/* Show Threads profile link for all cafe types */}
            {(userInfo?.profile?.username || threadsUrl) ? (
              <ButtonBasic
                size="lg"
                variant="contained"
                onClick={() => {
                  const url = userInfo?.profile?.username
                    ? `https://www.threads.net/@${userInfo.profile.username}`
                    : threadsUrl;
                  window.open(url, '_blank', 'noreferrer');
                }}
                sx={{ backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#111' } }}
                leftSection={<IconThreads size={16} />}
              >
                Threads 보기
              </ButtonBasic>
            ) : null}
            {cafeUrl ? (
              <ButtonBasic
                size="lg"
                variant="outlined"
                onClick={() => window.open(cafeUrl, '_blank', 'noreferrer')}
              >
                카페 사이트
              </ButtonBasic>
            ) : null}
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}


